<!--@format-->
<template>
    <div class="add-credit-card-widget" :class="componentClasses">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : 'Add Card Details' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <div class="payment edit">
                        <div class="form">
                            <div class="card-name-wrapper">
                                <app-input
                                    v-model="name"
                                    label="Card Name"
                                    placeholder="To distinguish your card from others, give it a distinctive name"
                                    :is-valid="getIsValidField('name')"
                                    :class="{ 'has-errors': !getIsValidField('name') }"
                                    required
                                    error="Required"
                                />
                            </div>
                            <label>Card Details</label>
                            <div class="card-details-graphic-wrapper">
                                <cc-graphic-svg />
                            </div>
                            <app-input
                                v-model="nameOnCard"
                                label="Name on Card"
                                placeholder=" "
                                :is-valid="getIsValidField('nameOnCard')"
                                :class="{ 'has-errors': !getIsValidField('nameOnCard') }"
                                required
                                error="Required"
                            />
                            <app-input
                                v-model="number"
                                label="Card Number"
                                placeholder=" "
                                auto-complete="off"
                                type="number"
                                :is-valid="getIsValidField('number')"
                                :class="{ 'has-errors': !getIsValidField('number') }"
                                required
                                error="Required"
                            />
                            <div class="card-details-wrapper">
                                <app-input
                                    v-model="expiration"
                                    label="Expiration Date"
                                    placeholder="MM/YY"
                                    :is-valid="getIsValidField('expiration')"
                                    :class="{ 'has-errors': !getIsValidField('expiration') }"
                                    required
                                    error="Required"
                                />
                                <app-input
                                    v-model="cvv"
                                    label="Security Code"
                                    placeholder=" "
                                    :is-valid="getIsValidField('cvv')"
                                    :class="{ 'has-errors': !getIsValidField('cvv') }"
                                    required
                                    error="Required"
                                />
                            </div>
                            <div class="card-details-wrapper">
                                <app-input
                                    v-model="address"
                                    label="Address"
                                    placeholder=" "
                                    :is-valid="getIsValidField('address')"
                                    :class="{ 'has-errors': !getIsValidField('address') }"
                                    required
                                    error="Required"
                                />
                                <app-input v-model="address2" label="Address 2" placeholder=" " />
                            </div>
                            <div class="card-details-wrapper">
                                <app-input
                                    v-model="city"
                                    label="City"
                                    placeholder=" "
                                    :is-valid="getIsValidField('city')"
                                    :class="{ 'has-errors': !getIsValidField('city') }"
                                    required
                                    error="Required"
                                />
                                <app-input
                                    v-model="state"
                                    label="State"
                                    placeholder=" "
                                    :is-valid="getIsValidField('state')"
                                    :class="{ 'has-errors': !getIsValidField('state') }"
                                    required
                                    error="Required"
                                />
                            </div>
                            <div class="card-details-wrapper">
                                <app-input
                                    v-model="zip"
                                    label="Zip"
                                    placeholder=" "
                                    :is-valid="getIsValidField('zip')"
                                    :class="{ 'has-errors': !getIsValidField('zip') }"
                                    required
                                    error="Required"
                                />
                                <app-input
                                    v-model="country"
                                    label="Country"
                                    placeholder=" "
                                    :is-valid="getIsValidField('country')"
                                    :class="{ 'has-errors': !getIsValidField('country') }"
                                    required
                                    error="Required"
                                />
                            </div>
                            <div class="form-conditions">
                                <div>
                                    By clicking save, you authorize GlassHive to charge the credit
                                    card specified for any amount owed for charges arising from your
                                    use of GlassHive's services and/or purchase of products from
                                    GlassHive, pursuant to GlassHive's website and terms, until this
                                    authorization is revoked. Your information will be saved to file
                                    for future transactions on my account. You may cancel this
                                    authorization at any time by removing this payment method.
                                </div>
                                <div>
                                    If you use GlassHive's services or purchase additional products
                                    periodically pursuant to GlassHive's terms, you authorize
                                    GlassHive to charge your credit card periodically. Payments that
                                    fall outside of the regular charges authorized will only be
                                    charged after your authorization is obtained.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" :disabled="processing" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <app-button class="save-btn" :loading="processing" @click="saveData">
                    Save
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppInput = () => import('@/components/common/app-input');
const CcGraphicSvg = () => import('@/components/svg/cc-graphic-svg.vue');
import { responsiveHelpers } from '@/scripts/helpers';
import CreditCardType from 'credit-card-type';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import EventBus from '@/scripts/event-bus.js';
import { ResponsiveMixin } from 'vue-responsive-components';
import { required } from 'vuelidate/lib/validators';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'add-credit-card-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-input': AppInput,
        'cc-graphic-svg': CcGraphicSvg,
    },

    mixins: [ResponsiveMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            processing: false,
            name: null,
            nameOnCard: null,
            number: null,
            expiration: null,
            cvv: null,
            cardType: 'default',
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            address: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            country: '',
        };
    },

    computed: {
        hasAgencyBillingPaymentPermission() {
            return (
                this.$store.getters.isBillingPaymentUser ||
                this.$store.getters.isOrganizationUser ||
                this.$store.getters.isMasterAgencyUser ||
                this.$store.getters.isAgencyAdmin
            );
        },

        isAgencyContext() {
            return this.$store.state.context.agency.id;
        },

        componentClasses() {
            return [responsiveHelpers.getBreakpointClasses(this)];
        },

        userConfig() {
            if (this.clientId) {
                return { userType: 'client', id: this.clientId };
            } else if (this.agencyId) {
                return { userType: 'agency', id: this.agencyId };
            } else {
                return { userType: 'masterAgency', id: this.agencyId };
            }
        },
    },

    breakpoints: responsiveHelpers.getBreakpoints(),

    watch: {
        number() {
            var cardTypes = CreditCardType(this.number).filter((card) => {
                return card.type;
            });

            if (cardTypes.length > 0) {
                this.cardType = cardTypes[0].type;
            }
        },
    },

    validations() {
        return {
            name: {
                required,
            },

            nameOnCard: {
                required,
            },

            number: {
                required,
            },

            expiration: {
                required,
                validateDate: (data) => this.validateDate(data),
            },

            cvv: {
                required,
            },

            address: {
                required,
            },

            address2: {},

            city: {
                required,
            },

            state: {
                required,
            },

            zip: {
                required,
            },

            country: {
                required,
            },
        };
    },

    created() {
        this.validateRoles();
    },

    methods: {
        validateRoles() {
            if (this.isAgencyContext && !this.hasAgencyBillingPaymentPermission) {
                this.closeWidget();
                const errorMessage =
                    'You need additional permissions in order to perform this action.';
                this.$helpers.showFailNotification(errorMessage, { level: LogLevel.INFO });
            }
        },

        validateDate(date) {
            const now = this.$moment();
            const isValid =
                this.$moment('01/' + date, 'DD-MM-YYYY').isValid &&
                this.$moment('01/' + date, 'DD-MM-YYYY').isAfter(now, 'month');
            return isValid;
        },

        getIsValidField(field) {
            const isInvalidValid = this.$v[field].$invalid && this.$v[field].$dirty;
            return !isInvalidValid;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        saveData() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            const request = {
                NickName: this.name,
                Name: this.nameOnCard,
                CreditCardNumber: this.number,
                PaymentType: 'card',
                ExpirationMonth: this.expiration.slice(-6, -3),
                ExpirationYear: this.expiration.slice(-2),
                Cvv: this.cvv,
                CardType: this.cardType,
                FullAddress:
                    this.address2.trim() !== '' ? this.address + ' ' + this.address2 : this.address,

                City: this.city,
                State: this.state,
                ZipCode: this.zip,
                Country: this.country,
            };
            this.processing = true;
            PaymentMethodsService.create(request, this.userConfig.id, this.userConfig.userType)
                .then(() => {
                    this.$helpers.showSuccessNotification('Billing saved successfully.');
                    EventBus.$emit('payment-method:update', true);
                    EventBus.$emit('add-credit-card-widget-to-user-widget', this.widget);
                    EventBus.$emit('add-credit-card-widget-to-cw-invite-user', this.widget);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your billing.',
                        { error },
                    );
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>

<style>
.add-credit-card-widget {
    color: #382153;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type='number'] {
    appearance: textfield;
}

.add-credit-card-widget .save-btn {
    width: 128px;
    height: 40px;
}

.add-credit-card-widget .card-details-wrapper {
    display: flex;
    gap: 20px;
}

.add-credit-card-widget .card-details-wrapper > * {
    width: 100%;
}

.add-credit-card-widget .form > *:not(:first-child) {
    margin-top: 20px;
}

.add-credit-card-widget .form-conditions {
    font: normal normal normal 14px Roboto;
    color: #877d88;
}

.add-credit-card-widget .form-conditions > *:not(:first-child) {
    margin-top: 20px;
}

.add-credit-card-widget .cancel-btn {
    background: transparent;
    border: 1px solid #cac6cb;
    font: normal normal medium 14px/24px Roboto;
    color: #877d88;
    min-width: 128px;
    min-height: 40px;
}

.add-credit-card-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.add-credit-card-widget .card-name-wrapper {
    padding-bottom: 20px;
    border-bottom: 2px solid #e9e9ef;
}

.add-credit-card-widget label {
    display: block;
    font: 500 16px/1em Roboto;
    margin-bottom: 7px;
    color: #382153;
}

.add-credit-card-widget .card-details-graphic-wrapper {
    display: flex;
    justify-content: center;
}

.add-credit-card-widget .app-input.error.has-errors input,
.add-credit-card-widget .gray-container.has-errors {
    border: 2px solid #f30070;
}

.add-credit-card-widget .app-input.error label {
    color: #382153;
}
</style>
